import { VideoCredits } from '../../../../redux/api/fymAPI.types';
import { isShortOfVideoCredits, videoCreditsLeft } from './videoCreditGate';

/** What "This costs" shows: a number of credits, "Free" (images) or "Pro plan" (a full video below Pro). */
export type CreditCostKind = 'credits' | 'free' | 'plan';

export interface CreditBlockView {
  costKind: CreditCostKind;
  /** Plan credits left plus pack credits. Null when the plan has no limit. */
  left: number | null;
  /** The balance after this make. Null when this make does not touch the balance. */
  afterThis: number | null;
  /** True when the balance (plan plus packs) is below the cost. */
  isShort: boolean;
  /** The plan credits left this month. Null when the plan has no limit. */
  planLeft: number | null;
  /** The share of the plan credits in the balance, 0 to 100, for the two-colour meter. */
  planPercent: number;
  /** The share of the pack credits in the balance, 0 to 100. */
  packPercent: number;
}

/**
 * The numbers of the credit block (design release-kit t5). The block, the sticky bar (t5b) and
 * the pre-click check (`useVideoCreditGate`) read the same credits answer, so they always agree.
 * `needsPlan` is true for an asset that needs a paid plan (a full video, the motion artwork).
 */
export function creditBlockView(credits: VideoCredits, cost: number, needsPlan: boolean): CreditBlockView {
  const costKind: CreditCostKind =
    needsPlan && credits.canRenderFullSongVideos === false ? 'plan' : cost <= 0 ? 'free' : 'credits';
  const left = videoCreditsLeft(credits);
  const isShort = costKind === 'credits' && isShortOfVideoCredits(credits, cost);
  const afterThis = costKind === 'credits' && left !== null && !isShort ? left - cost : null;
  const planLeft = credits.limit === null ? null : Math.max(0, credits.limit - credits.used);
  const packs = Math.max(0, credits.packBalance);
  const total = (planLeft ?? 0) + packs;
  const planPercent = total > 0 ? Math.round(((planLeft ?? 0) / total) * 100) : 0;
  const packPercent = total > 0 ? 100 - planPercent : 0;
  return { costKind, left, afterThis, isShort, planLeft, planPercent, packPercent };
}
